import { FC, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'

import { useMedia } from 'hooks/useMedia'
import { useDigitsContext } from 'context/digits-context'
import { usePlayerContext } from 'context/player-context'
import { breakpoints } from 'styles/mq'
import { Audio } from './audio'
import { Display } from './display'
import { Knob } from './knob'
import { Timeline } from './timeline'
import { DisplayMobile } from './display-mobile'
import { TimelineMobile } from './timeline-mobile'
import RadioImageSrc from './assets/radio-large.png'
import { HelpAnimation } from './help-animation'
import { radioHelpData } from './helpers'
import {
  DebugLine,
  DebugValue,
  RadioContainer,
  RadioImg,
  TimelineButtons,
  MuteButton,
  View,
} from './styled'

let timeoutId: ReturnType<typeof setTimeout>

export const Radio: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { year } = useParams()
  const { width, mapCoordsToYear, mapYearToCoords } = useDigitsContext()
  const {
    permissionsGranted,
    isMuted,
    toggleMuted,
    radioProgress,
    radioWave,
    setRadioWave,
    isHelpDone,
    makeHelpDone,
  } = usePlayerContext()
  const isMobile = useMedia([breakpoints.smallOnly], [true], false)
  const audioFileUrl = mapCoordsToYear(radioWave)
  const scrollRef = useRef<HTMLDivElement>(null)
  const roundedValue = useMemo(() => Math.round(radioWave), [radioWave])
  const yearPath = useMemo(() => `year/${audioFileUrl}`, [audioFileUrl])
  const [canPlaySound, setCanPlaySound] = useState(false)
  const isHomepage = location.pathname === '/'
  const isAbout = location.pathname === '/about'

  const handleScroll = useCallback(
    (delta: number) => {
      radioProgress.set(
        Math.min(width, Math.max(radioProgress.get() + delta, 0))
      )
    },
    [width, radioProgress]
  )

  const scrollManually = useCallback(
    (direction: number) => {
      handleScroll(10 * direction)
    },
    [handleScroll]
  )

  useEffect(() => {
    if (isHelpDone || !isHomepage) {
      setCanPlaySound(true)
    }
  }, [isHelpDone, isHomepage])

  useEffect(() => {
    setRadioWave(radioProgress.get())
  }, [year, radioProgress])

  useEffect(() => {
    const unsubscribe = radioProgress.onChange(setRadioWave)
    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (year && audioFileUrl !== year && canPlaySound) {
      const newValue = mapYearToCoords(year)

      if (newValue) {
        radioProgress.set(newValue)
      }
    }
  }, [year, canPlaySound])

  useEffect(() => {
    clearTimeout(timeoutId)
    if (audioFileUrl && !isHomepage && location.pathname !== yearPath) {
      timeoutId = setTimeout(() => navigate(yearPath), 500)
    }
  }, [audioFileUrl, isHomepage, yearPath])

  return (
    <>
      <Audio
        year={audioFileUrl}
        isHomepage={isHomepage}
        canPlaySound={canPlaySound}
      />

      {isHomepage ? (
        <View>
          <RadioContainer>
            {isMobile ? (
              <>
                <DisplayMobile
                  progress={radioProgress}
                  onScroll={handleScroll}
                />
              </>
            ) : (
              <>
                <RadioImg src={RadioImageSrc} alt="" />
                <Display progress={radioProgress} onScroll={handleScroll} />
                <Knob
                  ref={scrollRef}
                  progress={radioProgress}
                  onScroll={handleScroll}
                />
              </>
            )}
            <TimelineButtons>
              <button onClick={() => scrollManually(-1)}>-</button>
              <button onClick={() => scrollManually(1)}>+</button>
            </TimelineButtons>

            <MuteButton isMuted={isMuted}>
              <button
                title={isMuted ? 'Включить звук' : 'Выключить звук'}
                onClick={toggleMuted}
              />
            </MuteButton>

            <table
              style={{ position: 'absolute', top: '100%', display: 'none' }}
            >
              <tbody>
                <DebugLine as="tr">
                  <td style={{ width: '10em' }}>Currently Playing:</td>
                  <td style={{ width: '5em' }}>
                    <DebugValue>{audioFileUrl || '–'}</DebugValue>
                  </td>
                </DebugLine>
                <DebugLine as="tr">
                  <td>Current value:</td>
                  <td>
                    <DebugValue>{roundedValue}</DebugValue>
                  </td>
                </DebugLine>
              </tbody>
            </table>

            {isHomepage && permissionsGranted && !isHelpDone && (
              <HelpAnimation
                isHelpDone={isHelpDone}
                makeHelpDone={makeHelpDone}
                helpData={radioHelpData}
              />
            )}
          </RadioContainer>
        </View>
      ) : (
        !isAbout && (
          <>
            {isMobile ? (
              <TimelineMobile
                progress={radioProgress}
                onScroll={handleScroll}
              />
            ) : (
              <Timeline progress={radioProgress} onScroll={handleScroll} />
            )}
          </>
        )
      )}

      <Outlet />
    </>
  )
}
